import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useAssetTypes } from '../../hooks/useQueries'
import { getLocalizedName } from '../../lib/localization'
import { pick, type KPIDefinition } from './benchmark-utils'

/**
 * The registry and the labels the benchmark prints: KPI definitions come from
 * GET /kpis (never duplicated in i18n), trade names from the organization's own
 * asset types.
 */
export function useBenchmarkLabels(org: string) {
    const { t, i18n } = useTranslation('benchmark')
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en'
    const kpisQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/benchmark/kpis', {
        params: { path: { orgCode: org }, query: { family: 'cost' } },
    })
    const { data: assetTypes } = useAssetTypes(org)

    const defs = useMemo(() => (kpisQuery.data?.data ?? []) as KPIDefinition[], [kpisQuery.data])
    const byCode = useMemo(() => new Map(defs.map((d) => [d.code, d])), [defs])
    const typeNames = useMemo(() => {
        const m = new Map<string, string>()
        for (const at of assetTypes ?? []) {
            m.set(at.code, getLocalizedName(at.name as Record<string, string>, lang) || at.code)
        }
        return m
    }, [assetTypes, lang])

    const tradeLabel = useCallback(
        (key: string) => (key === 'unassigned' ? t('trade.unassigned') : typeNames.get(key) ?? key),
        [t, typeNames],
    )
    const kpiLabel = useCallback((code: string) => pick(byCode.get(code)?.label, lang) || code, [byCode, lang])

    return { defs, byCode, lang, tradeLabel, kpiLabel, loading: kpisQuery.isLoading }
}
